import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { ApiSuccess } from '../lib/api';
import { useCreateBook, useBooks } from '../hooks/useBooks';
import { useCredits } from '../hooks/useCredits';
import { useUserProfile, isActiveSubscriber, isCreatorPlus } from '../hooks/useUserProfile';
import { Navbar } from '../components/layout/Navbar';
import { UpgradeModal } from '../components/ui/UpgradeModal';
import { OutOfCreditsModal } from '../components/ui/OutOfCreditsModal';

const newBookSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200),
  genre: z.enum(['romance', 'thriller', 'fantasy', 'scifi', 'mystery', 'self-help', 'horror', 'historical', 'literary-fiction', 'adventure', 'young-adult', 'childrens', 'dystopian', 'paranormal', 'western', 'satire', 'short-stories', 'biography', 'autobiography', 'memoir', 'business', 'true-crime', 'history', 'science', 'travel', 'cooking', 'religion', 'philosophy', 'psychology']),
  premise: z.string().min(50, 'Premise must be at least 50 characters').max(2000, 'Premise must be under 2000 characters'),
  targetWordCount: z.union([z.literal(30000), z.literal(50000), z.literal(80000)]),
  toneStyle: z.enum(['commercial', 'literary', 'pulp', 'creative']),
  targetAudience: z.enum(['ya', 'new-adult', 'adult']),
  contentRating: z.enum(['clean', 'moderate', 'mature']),
  authorPersonaId: z.string().optional(),
  seriesId: z.string().optional(),
});

type NewBookFormData = z.infer<typeof newBookSchema>;

export function NewBook() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedSeriesId = searchParams.get('seriesId') ?? undefined;
  const createBook = useCreateBook();
  const { data: profile, isLoading: profileLoading } = useUserProfile();
  const { data: books, isLoading: booksLoading } = useBooks();
  const { data: credits } = useCredits();

  // Wait for both entitlement queries before deciding which UI to show
  const entitlementLoading = profileLoading || booksLoading;

  const isFreeFirstBook =
    !entitlementLoading &&
    profile !== undefined &&
    !isActiveSubscriber(profile) &&
    (books ?? []).length === 0;

  // Free user who already has ≥1 book — must not see the form at all
  const isFreeLimitReached =
    !entitlementLoading &&
    profile !== undefined &&
    !isActiveSubscriber(profile) &&
    (books ?? []).length >= 1;

  // Paid subscriber with 0 credits — show out-of-credits modal
  const isPaidOutOfCredits =
    !entitlementLoading &&
    isActiveSubscriber(profile) &&
    credits !== undefined &&
    credits.creditBalance === 0;

  const [creditsModalOpen, setCreditsModalOpen] = useState(false);
  useEffect(() => {
    if (isPaidOutOfCredits) setCreditsModalOpen(true);
  }, [isPaidOutOfCredits]);

  // M-1: fetch personas for author persona dropdown
  const { data: personasData } = useQuery({
    queryKey: ['personas'],
    queryFn: async () => {
      const res = await api.get<ApiSuccess<{ personas: Array<{ id: string; penName: string }> }>>('/personas');
      const raw = res.data.data?.personas;
      return { personas: Array.isArray(raw) ? raw : [] };
    },
  });

  // M-2: fetch series for series dropdown
  const { data: seriesData } = useQuery({
    queryKey: ['series'],
    queryFn: async () => {
      const res = await api.get<ApiSuccess<{ series: Array<{ id: string; title: string }> }>>('/series');
      return res.data.data ?? null;
    },
  });

  const personas = personasData?.personas ?? [];
  const seriesList = seriesData?.series ?? [];

  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<NewBookFormData>({
    resolver: zodResolver(newBookSchema),
    defaultValues: {
      genre: 'fantasy',
      targetWordCount: 50000,
      toneStyle: 'commercial',
      targetAudience: 'adult',
      contentRating: 'clean',
      seriesId: preselectedSeriesId,
    },
  });

  const premise = watch('premise', '');

  // Auto-select the only persona if exactly one exists — must be after useForm
  useEffect(() => {
    if (personas.length === 1) {
      setValue('authorPersonaId', personas[0]!.id);
    }
  }, [personas, setValue]);
  const [isCreative, setIsCreative] = useState(false);
  const [showUpgradeHint, setShowUpgradeHint] = useState(false);
  const isCreatorPlusUser = isCreatorPlus(profile);

  const onSubmit = async (data: NewBookFormData) => {
    if (isPaidOutOfCredits) {
      setCreditsModalOpen(true);
      return;
    }
    const result = await createBook.mutateAsync({ ...data, toneStyle: isCreative ? 'creative' : data.toneStyle });
    if (preselectedSeriesId) {
      void navigate(`/series/${preselectedSeriesId}`);
    } else {
      void navigate(`/books/${result.bookId}`);
    }
  };

  // Loading state — entitlement not yet resolved
  if (entitlementLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar variant="authed" />
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  // Gate: free user who already has ≥1 book — upgrade modal only, no form
  if (isFreeLimitReached) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar variant="authed" />
        <UpgradeModal
          open={true}
          onClose={() => void navigate('/dashboard')}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar variant="authed" />
      <OutOfCreditsModal open={creditsModalOpen} onClose={() => setCreditsModalOpen(false)} />

      <main className="max-w-3xl mx-auto px-6 py-8">
        <h1 className="text-2xl font-bold text-foreground mb-2">Create New Book</h1>
        <p className="text-muted-foreground mb-8">
          Describe your story and your AI co-author will write the full novel. 1 credit required.
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Title */}
          <div>
            <label htmlFor="book-title" className="block text-sm font-medium text-foreground mb-1">Book Title</label>
            <input
              id="book-title"
              {...register('title')}
              className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="e.g. The Shadow of Tomorrow"
            />
            {errors.title && <p className="mt-1 text-sm text-destructive">{errors.title.message}</p>}
          </div>

          {/* Genre */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Genre</label>
            <select
              {...register('genre')}
              className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <optgroup label="Fiction">
                <option value="literary-fiction">Literary Fiction</option>
                <option value="mystery">Mystery</option>
                <option value="thriller">Thriller</option>
                <option value="horror">Horror</option>
                <option value="romance">Romance</option>
                <option value="fantasy">Fantasy</option>
                <option value="scifi">Science Fiction</option>
                <option value="historical">Historical Fiction</option>
                <option value="adventure">Adventure</option>
                <option value="young-adult">Young Adult</option>
                <option value="childrens">Children's</option>
                <option value="dystopian">Dystopian</option>
                <option value="paranormal">Paranormal</option>
                <option value="western">Western</option>
                <option value="satire">Satire</option>
                <option value="short-stories">Short Stories</option>
              </optgroup>
              <optgroup label="Non-Fiction">
                <option value="biography">Biography</option>
                <option value="autobiography">Autobiography</option>
                <option value="memoir">Memoir</option>
                <option value="self-help">Self-Help</option>
                <option value="business">Business</option>
                <option value="true-crime">True Crime</option>
                <option value="history">History</option>
                <option value="science">Science</option>
                <option value="travel">Travel</option>
                <option value="cooking">Cooking</option>
                <option value="religion">Religion</option>
                <option value="philosophy">Philosophy</option>
                <option value="psychology">Psychology</option>
              </optgroup>
            </select>
          </div>

          {/* Premise */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Story Premise
              <span className="ml-2 text-xs text-muted-foreground">({premise.length}/2000 chars, min 50)</span>
            </label>
            <textarea
              {...register('premise')}
              rows={6}
              className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              placeholder="Describe your story: the main characters, the world, the central conflict, and what makes it unique. The more detail you provide, the better your novel will be..."
            />
            {errors.premise && <p className="mt-1 text-sm text-destructive">{errors.premise.message}</p>}
          </div>

          {/* Target Word Count — Controller-based for correct RHF numeric value tracking */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Target Length</label>
            {isFreeFirstBook ? (
              <div className="p-4 border border-primary/40 rounded-md bg-primary/5 flex items-start gap-3">
                <div className="mt-0.5 text-primary text-lg leading-none">★</div>
                <div>
                  <div className="font-medium text-foreground text-sm">Chapter Preview</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Your first chapter will be generated free — no credit required. Upgrade to unlock full-length novels.
                  </div>
                </div>
              </div>
            ) : (
              <Controller
                name="targetWordCount"
                control={control}
                render={({ field }) => (
                  <div className="grid grid-cols-3 gap-3">
                    {([30000, 50000, 80000] as const).map((wc) => (
                      <label
                        key={wc}
                        className={`flex items-center gap-2 p-3 border rounded-md cursor-pointer hover:bg-accent/50 transition-colors ${
                          field.value === wc
                            ? 'border-primary bg-primary/10'
                            : 'border-border'
                        }`}
                      >
                        <input
                          type="radio"
                          value={wc}
                          checked={field.value === wc}
                          onChange={() => field.onChange(wc)}
                          className="sr-only"
                        />
                        <div>
                          <div className="font-medium text-foreground text-sm">{(wc / 1000).toFixed(0)}K words</div>
                          <div className="text-xs text-muted-foreground">
                            {wc === 30000 ? '~10 chapters' : wc === 50000 ? '~16 chapters' : '~22 chapters'}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              />
            )}
            {errors.targetWordCount && (
              <p className="mt-1 text-sm text-destructive">{errors.targetWordCount.message}</p>
            )}
          </div>

          {/* Tone & Audience Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Writing Style</label>
              <select
                {...register('toneStyle')}
                className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="commercial">Commercial (accessible, page-turning)</option>
                <option value="literary">Literary (character-driven, nuanced)</option>
                <option value="pulp">Pulp (action-packed, fast-paced)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Target Audience</label>
              <select
                {...register('targetAudience')}
                className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="ya">Young Adult (13–17)</option>
                <option value="new-adult">New Adult (18–25)</option>
                <option value="adult">Adult (25+)</option>
              </select>
            </div>
          </div>

          {/* Content Rating */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Content Rating</label>
            <select
              {...register('contentRating')}
              className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="clean">Clean (no profanity, no explicit content)</option>
              <option value="moderate">Moderate (mild language, fade-to-black romance)</option>
              <option value="mature">Mature (adult content, explicit allowed)</option>
            </select>
          </div>

          {/* Author Persona — M-1 */}
          {personas.length > 0 && (
            <div>
              <label htmlFor="author-persona" className="block text-sm font-medium text-foreground mb-1">Author Persona (optional)</label>
              <select
                id="author-persona"
                {...register('authorPersonaId')}
                className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">None — use my account name</option>
                {personas.map((p) => (
                  <option key={p.id} value={p.id}>{p.penName}</option>
                ))}
              </select>
            </div>
          )}

          {/* Add to Series — M-2 */}
          {(seriesList.length > 0 || !!preselectedSeriesId) && (
            <div>
              <label htmlFor="book-series" className="block text-sm font-medium text-foreground mb-1">Add to Series (optional)</label>
              <select
                id="book-series"
                {...register('seriesId')}
                className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Not part of a series</option>
                {seriesList.map((s) => (
                  <option key={s.id} value={s.id}>{s.title}</option>
                ))}
              </select>
            </div>
          )}

          {createBook.error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-destructive">
              {(createBook.error as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message ?? 'Failed to create book. Please try again.'}
            </div>
          )}

          <div className="flex flex-col items-start gap-2 pt-2">
            <div className="relative group">
              <button
                type="button"
                onClick={() => {
                  if (!isCreatorPlusUser) { setShowUpgradeHint((v) => !v); return; }
                  setIsCreative((v) => !v);
                  setShowUpgradeHint(false);
                }}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  isCreative && isCreatorPlusUser
                    ? 'bg-violet-500/15 border-violet-500/50 text-violet-400'
                    : isCreatorPlusUser
                      ? 'bg-transparent border-border text-muted-foreground hover:text-foreground hover:border-foreground/30'
                      : 'bg-transparent border-border text-muted-foreground cursor-pointer opacity-70'
                }`}
              >
                <span>✨</span>
                Most Creative
                {!isCreatorPlusUser && <span className="text-xs text-muted-foreground/60">· Creator+</span>}
                {isCreatorPlusUser && (
                  <span className={`w-2 h-2 rounded-full ${isCreative ? 'bg-violet-400' : 'bg-muted-foreground/40'}`} />
                )}
              </button>

              {/* Hover tooltip */}
              <div className="absolute bottom-full left-0 mb-2 w-64 p-3 bg-popover border border-border rounded-lg shadow-lg text-xs text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                Pushes the AI toward unconventional structure, unexpected POV shifts, and boundary-pushing prose. Great for literary experiments — less predictable than standard modes.
                {!isCreatorPlusUser && (
                  <span className="block mt-1.5 text-violet-400 font-medium">Requires Creator plan.</span>
                )}
              </div>
            </div>

            {/* Upgrade hint — shown when non-Creator clicks */}
            {showUpgradeHint && !isCreatorPlusUser && (
              <p className="text-xs text-muted-foreground">
                Most Creative mode is available on the{' '}
                <Link to="/pricing" className="text-violet-400 hover:text-violet-300 underline underline-offset-2">
                  Creator plan
                </Link>
                . Upgrade to unlock it.
              </p>
            )}
          </div>

          <div className="flex items-center gap-4">
            <button
              type="submit"
              disabled={isSubmitting || createBook.isPending}
              className="flex-1 py-3 px-6 bg-primary text-primary-foreground font-semibold rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting || createBook.isPending
                ? 'Creating...'
                : isFreeFirstBook
                  ? 'Write My First Chapter (Free)'
                  : 'Write My Novel (1 credit)'}
            </button>
            <Link
              to="/dashboard"
              className="px-4 py-3 border border-border text-foreground rounded-md hover:bg-accent transition-colors text-sm"
            >
              Cancel
            </Link>
          </div>
        </form>
      </main>
    </div>
  );
}
